import * as React from 'react';
import api from "../utils/api";
import * as util from "../utils/util";
import { fetchUserSex } from "../utils/bodyModel";
import { useLocation, useNavigate } from "react-router-dom";

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from "@mui/material/TextField";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import { styled } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import PersonIcon from '@mui/icons-material/Person';
import HttpsIcon from '@mui/icons-material/Https';
import Loginbg from '../assets/images/loginbg.png';
import {useRef} from "react";



/**
 * permission/login 의 message 파싱.
 *
 * 응답이 "이름/userCusID/stressSurvey/ownerFamID/hraSurvey/userID/…" 처럼
 * '/' 로 이어 붙인 문자열이라, 이름에 '/' 가 들어가면 이후 값이 전부 한 칸씩 밀린다.
 * 뒤쪽 필드(userID·ownerFamID)는 위치가 고정이므로 **뒤에서부터** 센다.
 * 남는 앞부분을 이름으로 되돌려 붙이면 이름에 '/' 가 있어도 나머지가 어긋나지 않는다.
 *
 * 필드 수가 예상과 다르면(서버가 형식을 바꾼 경우) 앞에서 세던 기존 방식으로 돌아간다.
 */
export const parseLoginMessage = (message: string) => {
  const parts = String(message ?? '').split('/');

  // 위치로만 세는 기존 방식 (형식을 못 알아본 경우의 폴백)
  const byPosition = () => ({
    userName: parts[0] ?? '',
    userCusID: parts[1] ?? '',
    stressSurvey: parts[2] ?? '',
    ownerFamID: parts[3] ?? '',
    hraSurvey: parts[4] ?? '',
    userID: parts[5] ?? '',
  });

  // ownerFamID 는 'FAM#1013' 처럼 접두사가 있어 위치를 되찾는 기준점이 된다.
  // 뒤에서 세는 방식은 쓸 수 없다 — 응답 끝에 회사ID 등 개수가 정해지지 않은 값이 더 붙는다.
  const famIndex = parts.findIndex((p) => /^FAM#/i.test(p));
  if (famIndex < 3) return byPosition(); // 이름·userCusID·stressSurvey 자리가 안 나오면 폴백

  return {
    // 이름이 '홍/길동' 처럼 쪼개졌으면 도로 이어 붙인다
    userName: parts.slice(0, famIndex - 2).join('/'),
    userCusID: parts[famIndex - 2] ?? '',
    stressSurvey: parts[famIndex - 1] ?? '',
    ownerFamID: parts[famIndex] ?? '',
    hraSurvey: parts[famIndex + 1] ?? '',
    userID: parts[famIndex + 2] ?? '',
  };
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [customerId, setCustomerId] = React.useState('');
  const [customerPw, setCustomerPw] = React.useState('');
  // 응답을 기다리는 동안 버튼을 잠근다 (연타로 같은 요청이 중복되지 않도록)
  const [busy, setBusy] = React.useState(false);

  /**
   * 로그인 성공 후 갈 곳.
   * RequireLogin 이 막아세운 경우 원래 가려던 경로를 state.from 으로 넘겨준다.
   * 로그인 화면 자신이나 외부 URL 로는 보내지 않는다.
   */
  const redirectTo = (): string => {
    const from = (location.state as any)?.from;
    if (typeof from !== 'string' || !from.startsWith('/') || from.startsWith('//')) return '/';
    if (from.toLowerCase().startsWith('/login')) return '/';
    return from;
  };

  // 아이디 / 비밀번호 입력 후 엔터로도 로그인 (form submit)
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    login();
  }

  function login() {
    if (busy) return;
    if (customerId.trim() === '' || customerPw === '') {
      alert("아이디와 비밀번호를 입력해 주세요.");
      return;
    }
    setBusy(true);
    api.post('permission/login', { level: 'CUSTOMER', id: customerId, password: customerPw }).then(res => {
      if (res.data.message == "FAILED") {
        alert("가입하지 않은 아이디이거나, 잘못된 비밀번호입니다.");
        setBusy(false);
      } else {
        const info = parseLoginMessage(res.data.message);
        localStorage.setItem("token", res.data.data);
        localStorage.setItem("userID", info.userID);
        localStorage.setItem("userName", info.userName);
        localStorage.setItem("userCusID", info.userCusID);
        localStorage.setItem("stressSurvey", info.stressSurvey);
        localStorage.setItem("ownerFamID", info.ownerFamID);
        localStorage.setItem("hraSurvey", info.hraSurvey);

        // 3D 모델 성별을 미리 조회해 캐시에 채워둔다 (실패해도 무시).
        //
        // 디지털트윈·심리 화면은 성별이 확정된 뒤에야 13MB 모델을 받기 시작한다.
        // 여기서 미리 받아두지 않으면 화면을 열 때 getProfile 왕복이 다운로드
        // 앞에 직렬로 붙어서, 모바일에서는 그만큼 첫 표시가 늦어진다.
        // (모델 자체를 미리 받지는 않는다 — 3D 화면을 안 여는 사용자에게
        //  로그인만으로 13MB 를 쓰게 할 수 없다.)
        fetchUserSex().catch(function () { /* 화면에서 다시 시도한다 */ });

        // 심리상담 메뉴 노출 관리 — 메뉴 표시용 부가 정보다.
        // 이 호출이 실패해도 로그인은 성공한 것이므로 이동을 막아선 안 된다.
        // (기존에는 navigate 가 then 안에만 있어서, 이 호출이 실패하면
        //  로그인에 성공해도 로그인 화면에 그대로 남았다.)
        api.post('customer/api/v1/checkCounselTarget', { customerId: info.userCusID }).then(res => {
          localStorage.setItem("openConsultationReservation", res.data?.data?.isTarget);
        }).catch(function (error) {
          console.log(error);
        }).finally(function () {
          // 막아세워진 화면이 있으면 그리로, 없으면 홈으로
          navigate(redirectTo(), { replace: true });
        });
      }
    }).catch(function (error) {
      // 실패를 조용히 넘기지 않는다 — 서버 정지·타임아웃(5초)·네트워크 단절에서
      // 아무 안내가 없으면 사용자는 버튼이 먹지 않는 것으로만 느낀다.
      console.log(error);
      setBusy(false);
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        alert("가입하지 않은 아이디이거나, 잘못된 비밀번호입니다.");
      } else if (error?.code === 'ECONNABORTED') {
        alert("서버 응답이 지연되고 있습니다. 잠시 후 다시 시도해 주세요.");
      } else {
        alert("로그인 중 오류가 발생했습니다. 네트워크 상태를 확인해 주세요.");
      }
    });
  }

  function logout() {
    util.clearLoginStorage();
    alert("로그아웃 되었습니다.");
  }

  const Img = styled('img')({
    margin: 'auto',
    display: 'block',
    maxWidth: '100%',
    maxHeight: '100%',
  });
  const loginField = {
    width: '97%',
    '& label': {
      color: '#aaa',
    },
    '& label.Mui-focused': {
      color: '#ef8322',
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: '#ef8322',
    },
    '& .MuiInputBase-root:before':{
      borderBottomColor: '#bbb',
    }
  };

  const Logbg = {
    bgcolor: '#fff1ea', 
    height: '100vh', 
    minHeight:600,
    backgroundImage:`url(${Loginbg})`,
    backgroundPosition: 'top',
    backgroundSize: '100%',
    backgroundRepeat: 'no-repeat'
  }

  return (
    <Container sx={Logbg}>
      <Box sx={{ pt: 20, pb: 10, width: '70%', m: '0 auto' }}>
        <Img alt="DualLogo" src="/dualLogin.png" />
      </Box>

      <Stack component="form" onSubmit={handleSubmit} sx={{ marginTop: 4, width: '90%', ml: 'auto', mr: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', mb:1 }}>
          <PersonIcon sx={{ color: '#FC9888', mr: 1, my: 0.5 }} />
          <TextField label="ID" variant="standard"
            autoComplete="username"
            onChange={(e) => { setCustomerId(e.target.value) }} sx={loginField} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
          <HttpsIcon sx={{ color: '#FC9888', mr: 1, my: 0.5 }} />
          <TextField label="Password" variant="standard"
            type={'password'}
            autoComplete="current-password"
            onChange={(e) => { setCustomerPw(e.target.value) }} sx={loginField} />
        </Box>

        <Button type="submit" variant="contained" disabled={busy} sx={BottomBtnStyle}>
          {busy ? '로그인 중…' : 'Login'}
        </Button>

        <Stack direction={'row'} justifyContent={'center'} >
          <Button onClick={() => navigate('../Join/JoinAgree')} sx={{fontSize:14, color:'#777'}}>회원가입</Button>
            <Divider orientation="vertical" variant="middle" flexItem />
          <Button onClick={() => navigate('../Join/FindId')}  sx={{fontSize:14,color:'#777'}}>아이디 찾기</Button>
            <Divider orientation="vertical" variant="middle" flexItem />
          <Button onClick={() => navigate('../Join/FindPassword')}  sx={{fontSize:14,color:'#777'}}>비밀번호 찾기</Button>
        </Stack>

      </Stack>
    </Container>
  );
}


const BottomBtnStyle = {
  width: '100%',
  bgcolor: '#ef8322',
  fontSize: 18,
  fontWeight: 700,
  borderRadius: '10px',
  boxSizing: 'border-box',
  color: '#fff',
  border: '1px solid #ef8322',
  mt: 4,
  mb:1,
  ':hover': {
    backgroundColor: '#EF8322',
    borderColor: '#EF8322',
    boxShadow: 'none',
    color: '#fff'
  },
  ':active': {
    boxShadow: 'none',
    backgroundColor: '#EF8322',
    borderColor: '#EF8322',
  },
  ':focus': {
    boxShadow: 'none',
  },
};

export default Login;